// Fundamentals quick-add templates (discoverability). Each template inserts a
// NORMAL Expression block preconfigured with a Fundamental (SF1) feature operand —
// NO new engine BlockType. Internally identical to what the operand dropdown builds:
// leftSource:'feature', leftFeature:'fund.<metric>', a comparison operator, and a
// static threshold. Metric keys must match the SF1 feature-series adapter
// (src/engine/fundamentals/sf1FeatureSeries.ts → keys are `fund.<metric>`).
import type { Block, BlockData } from '../../types';
import { BlockType } from '../../types';

export interface FundamentalTemplate {
  id: string;
  label: string;          // human label shown on the quick-add card
  feature: string;        // 'fund.<metric>' — MUST match the SF1 adapter keys
  op: '<' | '>' | '<=' | '>=';
  value: number;
  hint: string;           // one-line description
}

// Predeclared quick-adds (value / quality / balance-sheet / growth families).
export const FUNDAMENTAL_TEMPLATES: FundamentalTemplate[] = [
  { id: 'pe-lt-25',        label: 'P/E < 25',              feature: 'fund.pe',          op: '<', value: 25,   hint: 'Value — price/earnings below 25' },
  { id: 'roe-gt-15',       label: 'ROE > 15%',             feature: 'fund.roe',         op: '>', value: 0.15, hint: 'Quality — return on equity above 15%' },
  { id: 'grossmargin-gt-40', label: 'Gross margin > 40%',  feature: 'fund.grossmargin', op: '>', value: 0.40, hint: 'Quality — gross margin above 40%' },
  { id: 'de-lt-1',         label: 'Debt/Equity < 1.0',     feature: 'fund.de',          op: '<', value: 1.0,  hint: 'Balance sheet — leverage below 1.0' },
  { id: 'revgrowth-gt-10', label: 'Revenue growth > 10%',  feature: 'fund.revgrowth',   op: '>', value: 0.10, hint: 'Growth — YoY revenue growth above 10%' },
  { id: 'evebitda-lt-12',  label: 'EV/EBITDA < 12',        feature: 'fund.evebitda',    op: '<', value: 12,   hint: 'Value — EV/EBITDA below 12' },
];

export const FUNDAMENTAL_HELPER_NOTE =
  'Fundamental metrics use point-in-time SF1 data aligned by knowledge date.';

/**
 * Build a normal EXPRESSION block preconfigured for a fundamental condition.
 * Returns the SAME shape the operand dropdown produces — the engine reads it via
 * readOperand `source:'feature'`. NO new BlockType is introduced.
 */
export function buildFundamentalExpressionBlock(t: FundamentalTemplate, pos?: { x: number; y: number }): Block {
  const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `expr_${Math.random().toString(36).slice(2)}`;
  const jitter = () => Math.round(Math.random() * 120);
  const now = new Date();
  return {
    id,
    type: BlockType.EXPRESSION,
    position: pos ?? { x: 360 + jitter(), y: 200 + jitter() },
    data: {
      name: t.label,
      leftSource: 'feature',
      leftFeature: t.feature,
      operator: t.op,
      rightSource: 'static',
      rightValue: t.value,
    } as unknown as BlockData,
    connections: { inputs: [], outputs: [] },
    metadata: {
      version: '1.0.0',
      created: now,
      modified: now,
      locked: false,
      collapsed: false,
      author: 'User',
      description: `Fundamental condition: ${t.label} (SF1, point-in-time)`,
      tags: ['tsl', 'fundamental'],
    },
  };
}
